import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClientPoint,
  useFloating,
  UseFloatingReturn,
  useInteractions,
  UseInteractionsReturn,
} from '@floating-ui/react';
import { debounce } from 'lodash-es';
import { AnimatePresence } from 'motion/react';
import { TOTAL_HOURS } from '../../constants';
import { useZoomStore } from '../../store/zoom.store';
import { getHoverMinutesFromEvent, minutesToTimeStr } from '../../utils';
import { EventTooltipContent } from './event-tooltip-content';
import { TooltipContainer } from './tooltip-container';

type UseTooltipReturn = {
  hoverMinutes?: number;
  hoveredEventId?: string;
  onMouseMove: (x: number, y: number, hoveredEventId?: string) => void;
  hideTooltip: () => void;
  getReferenceProps: UseInteractionsReturn['getReferenceProps'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
  refs: UseFloatingReturn['refs'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
};

type HoverState = {
  hoverMinutes?: number;
  hoveredEventId?: string;
  x?: number;
  y?: number;
};

const STEADY_DELAY_MS = 600;
const MAX_TOOLTIP_DISTANCE_PX = 50;

export const useCalendarTooltip = (containerRef: React.RefObject<HTMLElement>): UseTooltipReturn => {
  const { resolvedRowHeightPx } = useZoomStore(['resolvedRowHeightPx']);
  const [hoverState, setHoverState] = useState<HoverState>({});
  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    elements: { reference: containerRef.current },
    middleware: [offset(20), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  // Explicit x/y (rather than the default automatic mouse tracking) so position only
  // updates at the throttled cadence below instead of re-rendering on every raw mousemove.
  const clientPoint = useClientPoint(context, { x: hoverState.x ?? null, y: hoverState.y ?? null });
  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint]);

  const tooltipStartPos = useRef<{ x: number; y: number } | null>(null);
  const showTooltipDebounced = useMemo(
    () =>
      debounce((clientX: number, clientY: number, hoveredEventId?: string) => {
        const totalMinutes = getHoverMinutesFromEvent(
          clientY,
          containerRef.current as HTMLElement,
          resolvedRowHeightPx
        );
        tooltipStartPos.current = { x: clientX, y: clientY };
        setHoverState({ hoverMinutes: totalMinutes, hoveredEventId, x: clientX, y: clientY });
      }, STEADY_DELAY_MS),
    [containerRef, resolvedRowHeightPx]
  );
  const hideTooltip = useCallback(() => {
    setHoverState({});
    tooltipStartPos.current = null;
    showTooltipDebounced.cancel();
  }, [showTooltipDebounced]);

  const onMouseMove = useCallback(
    (clientX: number, clientY: number, hoveredEventId?: string) => {
      if (hoverState.hoverMinutes === undefined) {
        // show the tooltip after the initial delay
        showTooltipDebounced(clientX, clientY, hoveredEventId);
        return;
      }

      // check if the mouse has moved too far from the initial tooltip position
      const tooltipStart = tooltipStartPos.current;
      const distanceFromTooltipStart = tooltipStart
        ? Math.hypot(clientX - tooltipStart.x, clientY - tooltipStart.y)
        : 0;

      if (distanceFromTooltipStart >= MAX_TOOLTIP_DISTANCE_PX) {
        hideTooltip();
        showTooltipDebounced(clientX, clientY, hoveredEventId);
        return;
      }

      const totalMinutes = getHoverMinutesFromEvent(clientY, containerRef.current as HTMLElement, resolvedRowHeightPx);
      setHoverState({ hoverMinutes: totalMinutes, hoveredEventId, x: clientX, y: clientY });
    },
    [containerRef, hideTooltip, hoverState.hoverMinutes, resolvedRowHeightPx, showTooltipDebounced]
  );
  useEffect(() => () => showTooltipDebounced.cancel(), [showTooltipDebounced]);

  return {
    hoverMinutes: hoverState.hoverMinutes,
    hoveredEventId: hoverState.hoveredEventId,
    onMouseMove,
    hideTooltip,
    getReferenceProps,
    getFloatingProps,
    refs,
    floatingStyles,
  };
};

interface Props {
  hoverMinutes?: number;
  hoveredEventId?: string;
  containerRef: UseFloatingReturn['refs']['setFloating'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getFloatingProps: UseInteractionsReturn['getFloatingProps'];
}

export const CalendarTooltip = memo(
  ({ hoverMinutes, hoveredEventId, containerRef, floatingStyles, getFloatingProps }: Props) => {
    const hoverTime = minutesToTimeStr(hoverMinutes ?? 0);
    const shouldShow = hoverMinutes !== undefined && hoverMinutes > 0 && hoverMinutes < TOTAL_HOURS * 60;

    return (
      <>
        <AnimatePresence>
          {shouldShow && (
            <FloatingPortal>
              <div
                ref={containerRef}
                style={floatingStyles}
                {...getFloatingProps()}
                css={{ pointerEvents: 'none' }}
                data-testid='calendar-tooltip'
              >
                <TooltipContainer>
                  <EventTooltipContent hoveredEventId={hoveredEventId} hoverTime={hoverTime} />
                </TooltipContainer>
              </div>
            </FloatingPortal>
          )}
        </AnimatePresence>
      </>
    );
  }
);
CalendarTooltip.displayName = 'CalendarTooltip';
